<template>
	<section class="compare-block">
		<div class="row row-header">
			<div class="columns column12 align-center">
				<h2>
					{{ header }}
				</h2>
			</div>
		</div>
		<div class="row">
			<div class="columns column12">
				<div class="carousel-wrapper">
					<div class="carousel-facilities">
						<div class="table-item" />
						<div class="table-item facility-item">
							<picture>
								<source :srcset="roomSizeIcon.imageWebp" type="image/webp" />
								<source :srcset="roomSizeIcon.image" />
								<img
									:src="roomSizeIcon.image"
									:alt="roomSizeIcon.imageAlt"
									class="facility-icon"
									loading="lazy"
								/>
							</picture>
							<h3>{{ roomSize }}</h3>
						</div>
						<div v-for="item in facilitiesItems" :key="item.ID" class="facility-item table-item">
							<picture>
								<source :srcset="item.iconWebp" type="image/webp" />
								<source :srcset="item.icon" />
								<img :src="item.icon" :alt="item.iconAlt" class="facility-icon" loading="lazy" />
							</picture>
							<h3>{{ item.header }}</h3>
						</div>
					</div>
					<bc-carousel
						ref="compareCarousel"
						class="compare-slider"
						:mouse-drag="false"
						:breakpoints="breakpoints"
						:model-value="0"
						:items-to-show="1.25"
						snap-align="start"
					>
						<template #addons>
							<bc-navigation class="slider-navigation">
								<template #next>
									<div class="slider-button slider-next" />
								</template>
								<template #prev>
									<div class="slider-button slider-prev" />
								</template>
							</bc-navigation>
						</template>
						<bc-slide v-for="room in items" :key="room.ID" class="room-item slider-item active">
							<div class="table-item">
								<nuxt-link :to="localePath(`/${room.filename}`)" class="button">
									{{ room.header }}
								</nuxt-link>
							</div>
							<div class="table-item">
								<span>{{ room.roomSize }}</span>
							</div>
							<div v-for="facility in facilitiesItems" :key="facility.ID" class="table-item">
								<div v-if="room.facilities.find((item) => item.ID === facility.ID)">
									<font-awesome-icon :icon="`fal fa-check`" size="1x" />
								</div>
							</div>
						</bc-slide>
					</bc-carousel>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
defineProps({
	header: { type: String, default: '' },
	roomSize: { type: String, default: '' },
	roomSizeIcon: {
		type: Object,
		default: () => {},
	},
	items: { type: Array, default: () => [] },
	facilitiesItems: { type: Array, default: () => [] },
});

const localePath = useLocalePath();

const compareCarousel = ref(null);

const breakpoints = {
	700: {
		itemsToShow: 2,
	},
	1080: {
		itemsToShow: 2,
	},
};
</script>

<style lang="scss" scoped>
.compare-block {
	background: none;
	margin: 0;

	h2 {
		color: var(--header-color-secondary);
	}

	.row {
		max-width: 1140px;
	}
}

.compare-slider {
	padding: 0 0 80px;
}

.slider-item {
	display: flex;
	flex-flow: column wrap;
}

.carousel-wrapper {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: flex-start;

	.carousel-facilities {
		width: 20%;

		.facility-item {
			display: flex;
			flex-flow: column wrap;
			justify-content: center;
			align-items: center;

			img {
				max-width: 25px;
			}
		}
	}

	.compare-slider {
		width: 80%;
	}
}

.table-item {
	width: 100%;
	height: 140px;
	display: flex;
	flex-flow: column wrap;
	justify-content: center;
	align-items: center;
	border-right: 1px solid var(--body-color);
	border-bottom: 1px solid var(--body-color);

	h3 {
		font-size: calc(var(--h4-font-size) - 4px);
		margin: 20px 0 0;
		text-align: center;
	}

	svg {
		font-size: 34px;
		color: var(--header-color-secondary);
	}

	span {
		font-size: 18px;
		color: var(--header-color-secondary);
	}

	&:first-child {
		border: none;
	}

	&:last-child {
		border-bottom: none;
	}
}

li:last-child {
	.table-item {
		border-right: none;
	}
}

/* stylelint-disable selector-class-pattern */
:deep(.carousel__viewport) {
	width: 100%;
}

:deep(.slider-navigation) {
	position: absolute;
	bottom: 0;
	left: 0;
	top: unset;
	transform: unset;
	display: none;
	width: 67px;
	height: 67px;
	margin: 0;
	padding: 0;

	&.carousel__next {
		left: unset;
		right: 0;
	}
}
/* stylelint-enable selector-class-pattern */

@media (max-width: 1080px) {
	/* stylelint-disable selector-class-pattern */
	:deep(.slider-navigation) {
		display: block;
	}
	/* stylelint-enable selector-class-pattern */

	.carousel-wrapper {
		.carousel-facilities {
			width: 36%;
		}

		.compare-slider {
			width: 64%;
		}

		.button {
			padding: 11px 30px;
		}
	}
}

@media (max-width: 700px) {
	.carousel-wrapper {
		.carousel-facilities {
			width: 34%;
		}

		.compare-slider {
			width: 66%;
		}

		.button {
			padding: 10px 24px;
		}
	}

	.table-item {
		height: 110px;

		h3 {
			font-size: calc(var(--h4-font-size) - 8px);
			margin: 15px 0 0;
		}

		svg {
			font-size: 30px;
			color: var(--header-color-secondary);
		}

		span {
			font-size: 16px;
			color: var(--header-color-secondary);
		}
	}
}
</style>
